/**
 * Single source of truth for bundled imagery.
 *
 * Ported from `kaira-mobile-app/src/theme/assets.ts`. On native these are
 * `require()` module ids; on the web they are paths under `public/assets`, so
 * the URLs stay identical between dev, build and CDN, and can be referenced
 * from `<img>`, CSS and OpenGraph tags alike.
 */

import type { ImageSource } from './types';

const A = '/assets';

/* -------------------------------------------------------------------------- */
/* Brand                                                                      */
/* -------------------------------------------------------------------------- */

export const brandName = 'KAYRA Care';

/**
 * The gold lioness emblem. Transparent background, safe on cream or dark surfaces.
 * Generated by brand/make-brand.js from brand/source/kayra-lioness-emblem.png.
 *
 * Beside the wordmark it is one wordmark-height tall; stacked above it, 1.1
 * wordmark-heights over a gap of 0.14 of its own height (the splash art's lockup).
 */
export const brandMark: ImageSource = `${A}/kayra-care-mark.png`;

/** "KAYRA" in deep forest green over a gold "CARE", for light surfaces. */
export const wordmark: ImageSource = `${A}/kayra-care-wordmark.png`;

/** The same wordmark with a cream "KAYRA", for the forest rail and dark panels. */
export const wordmarkLight: ImageSource = `${A}/kayra-care-wordmark-light.png`;

export const brandTagline = 'Better Services. A Happier You.';

/**
 * Intrinsic aspect ratios (width / height) of the shipped brand artwork: the
 * lioness emblem is 720 x 550 and the wordmark 1088 x 387.
 */
export const MARK_RATIO = 720 / 550;
export const WORDMARK_RATIO = 1088 / 387;

/* -------------------------------------------------------------------------- */
/* Editorial / marketing                                                      */
/* -------------------------------------------------------------------------- */

/** Full-bleed 2:3 portrait behind the login panel. 1024 x 1536. */
export const loginBackground: ImageSource = `${A}/loginpage-bg.png`;

export interface BannerAsset {
  id: string;
  image: ImageSource;
  eyebrow: string;
  title: string;
  subtitle: string;
  cta: string;
  /**
   * Which horizontal half of the photo holds the subject, so the text scrim can
   * be placed on the opposite side.
   */
  subjectSide: 'left' | 'right';
  href: string;
}

/**
 * Home carousel. All three frames are shot with the subject in the left half,
 * so every scrim sits on the right.
 */
export const banners: BannerAsset[] = [
  {
    id: 'banner_therapist',
    image: `${A}/therepiest-banner.png`,
    eyebrow: 'At your doorstep',
    title: 'Therapists who\narrive prepared',
    subtitle: 'Background-verified, trained in-house',
    cta: 'Book a therapist',
    subjectSide: 'left',
    href: '/professionals',
  },
  {
    id: 'banner_bridal',
    image: `${A}/bridle-banner.png`,
    eyebrow: 'Bridal studio',
    title: 'Your wedding day,\nflawlessly done',
    subtitle: 'Trial session included on every package',
    cta: 'Explore bridal',
    subjectSide: 'left',
    href: '/services/makeup',
  },
  {
    id: 'banner_salon',
    image: `${A}/salonluxury-banner.png`,
    eyebrow: 'Salon-grade luxury',
    title: 'Professional kit,\nsealed and sanitised',
    subtitle: 'Premium products on every visit',
    cta: 'See all services',
    subjectSide: 'left',
    href: '/services',
  },
];

/* -------------------------------------------------------------------------- */
/* Category photography                                                       */
/* -------------------------------------------------------------------------- */

export const categoryImages = {
  hair: `${A}/hair-section.png`,
  skin: `${A}/skin-section.png`,
  makeup: `${A}/makeup-section.png`,
  nails: `${A}/nails-service-icon.png`,
  /** An illustration (stones, towel, eucalyptus) until a massage photo is shot. */
  massage: `${A}/massage-section.png`,
} satisfies Record<string, ImageSource>;

export type CategoryImageKey = keyof typeof categoryImages;

/** Maps a `ServiceCategory.id` to its hero image. */
export const categoryImageById: Record<string, ImageSource> = {
  cat_hair: categoryImages.hair,
  cat_skin: categoryImages.skin,
  cat_makeup: categoryImages.makeup,
  cat_nails: categoryImages.nails,
  cat_massage: categoryImages.massage,
};

/**
 * Resolves a category to its image, falling back to the skin treatment shot,
 * which is the most neutral of the set.
 */
export function imageForCategory(categoryId: string): ImageSource {
  return categoryImageById[categoryId] ?? categoryImages.skin;
}

/* -------------------------------------------------------------------------- */
/* People                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Portraits for therapist avatars. The shipped asset set has no dedicated
 * headshots, so we reuse the frames where a therapist is the clear subject and
 * rotate deterministically — a given professional keeps the same face across
 * renders, pages and the admin panel.
 */
const portraitPool: ImageSource[] = [
  `${A}/therepiest-banner.png`,
  loginBackground,
  categoryImages.makeup,
  categoryImages.skin,
];

export function portraitForIndex(index: number): ImageSource {
  const pool = portraitPool;
  return pool[((index % pool.length) + pool.length) % pool.length]!;
}

/** Stable portrait for an arbitrary id (hashes the string into the pool). */
export function portraitForId(id: string): ImageSource {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return portraitForIndex(Math.abs(hash));
}

/** Open Graph fallback share image. */
export const ogFallback: ImageSource = `${A}/salonluxury-banner.png`;
